import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import api, { authLogin, authLogout, authSwitchTenant, clearAccessToken, refreshSession, setSessionLostHandler } from './api/axios';
import { reloadCurrentPage } from './reloadPage';

export const AuthContext = createContext(null);
export const LOGIN_PREFERENCES = 'jv.loginPreferences';
export const TENANT_SWITCH_EVENT = 'jv.tenantSwitch';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(false);
  const [switchError, setSwitchError] = useState('');
  const changingTenant = useRef(false);
  useEffect(() => {
    const changed = event => { if (event.key === TENANT_SWITCH_EVENT && event.newValue) reloadCurrentPage(); };
    window.addEventListener('storage', changed);
    return () => window.removeEventListener('storage', changed);
  }, []);
  useEffect(() => {
    let mounted = true;
    try { localStorage.removeItem('token'); } catch { /* Storage may be disabled. */ }
    setSessionLostHandler(() => { if (mounted) setUser(null); });
    (async () => {
      try {
        await refreshSession();
        const response = await api.get('/auth/me');
        if (mounted) setUser(response.data.user);
      } catch { if (mounted) setUser(null); }
      finally { if (mounted) setLoading(false); }
    })();
    return () => { mounted = false; setSessionLostHandler(() => {}); };
  }, []);
  const login = useCallback(async payload => {
    const identity = await authLogin(payload);
    try { localStorage.setItem(LOGIN_PREFERENCES, JSON.stringify({ tenant: payload.tenant.trim(), username: payload.username.trim() })); }
    catch { /* Preferences are optional when browser storage is unavailable. */ }
    setUser(identity);
    return identity;
  }, []);
  const logout = useCallback(async () => {
    await authLogout();
    clearAccessToken();
    setUser(null);
    // Discard selection IDs when changing identities; preferences remain.
    try { localStorage.removeItem('jv.businessContext'); } catch { /* Storage may be disabled. */ }
  }, []);
  const switchTenant = useCallback(async tenantId => {
    if (changingTenant.current || Number(tenantId) === user?.tenant?.id) return;
    changingTenant.current = true; setSwitching(true); setSwitchError('');
    try {
      const identity = await authSwitchTenant(tenantId);
      try {
        localStorage.removeItem('jv.businessContext');
        localStorage.setItem(LOGIN_PREFERENCES, JSON.stringify({ tenant: identity.tenant.name, username: identity.username }));
        localStorage.setItem(TENANT_SWITCH_EVENT, JSON.stringify({ tenant: identity.tenant.id, at: Date.now() }));
      } catch { /* Reload also reconstructs context when storage is unavailable. */ }
      reloadCurrentPage();
    } catch (error) {
      setSwitchError(error.response?.data?.error || 'Mandantenwechsel fehlgeschlagen. Bitte erneut versuchen.');
      setSwitching(false);
      throw error;
    } finally { changingTenant.current = false; }
  }, [user]);
  return <AuthContext.Provider value={{ user, loading, login, logout, switchTenant, switching, switchError }}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error('AuthProvider fehlt.');
  return value;
}
